// Unpublished Work © 2022-2026 Deere & Company.

import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import React from 'react';
import {
    getEmployeeStatusOptions,
    deleteEmployeeStatusOption
} from 'Services/membership-service';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {replaceTranslationNames} from 'Utils/translation-utils';
import EditLink from 'Ui/components/common/data-table/edit-link';
import AddButton from 'Ui/components/settings/common/add-button';
import {REMAP_STATUS_COLORS} from 'Common/constants/valid-hex-color';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';
import {Box} from '@deere/isg.component-library';

function getDataGridColumns(
    translations,
    openEmployeeStatusDialog,
    fetchStatusOptions
) {
    return [
        {
            headerName: translations.NAME,
            field: 'name',
            flex: 1,
            renderCell: (params) => {
                return (
                    <EditLink
                        onClick={() =>
                            openEmployeeStatusDialog({
                                status: params.row,
                                translations,
                                getUpdatedStatusOptions: fetchStatusOptions
                            })
                        }
                    >
                        {params.value}
                    </EditLink>
                );
            }
        },
        {
            headerName: translations.ONLINK_COLOR,
            field: 'color',
            flex: 1,
            renderCell: (params) => {
                const color = REMAP_STATUS_COLORS[params.value] || params.value;

                return (
                    <Box
                        alignItems='center'
                        display='flex'
                        height='100%'
                        justifyContent='start'
                        width='100%'
                    >
                        <div
                            className={`color-item status-color-item ${color}`}
                        />
                    </Box>
                );
            }
        },
        {
            headerName: translations.ONLINK_AVAILABLE_FOR_WORK,
            field: 'isAvailable',
            flex: 1,
            renderCell: (params) => {
                return params.value ? translations.yes : translations.no;
            }
        }
    ];
}

function getFilterComponent(
    translations,
    openEmployeeStatusDialog,
    fetchStatusOptions
) {
    return (
        <div className='extra-table-options'>
            <AddButton
                className='add-status-button'
                label={translations.ONLINK_ADD_STATUS}
                onClick={() => {
                    openEmployeeStatusDialog({
                        translations,
                        getUpdatedStatusOptions: fetchStatusOptions
                    });
                }}
            />
        </div>
    );
}

function EmployeeStatusesSettings(props) {
    const {
        closeConfirmation,
        openConfirmation,
        translations,
        openEmployeeStatusDialog
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(false);
    const [statusOptions, setStatusOptions] = React.useState([]);

    async function fetchStatusOptions(isMounted = () => true) {
        setLoading(true);

        const {employeeStatusOptions} = await getEmployeeStatusOptions();

        if (isMounted()) {
            setStatusOptions(employeeStatusOptions);
            setLoading(false);
        }
    }

    function deleteEmployeeStatus(employeeStatus) {
        openConfirmation({
            message: replaceTranslationNames(
                translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE,
                {
                    '0': translations.STATUS.toLowerCase()
                }
            ),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.STATUS
            }),
            async onContinue() {
                closeConfirmation();

                try {
                    await deleteEmployeeStatusOption(
                        employeeStatus.employeeStatusOptionId
                    );

                    await fetchStatusOptions();
                } catch {
                    addToast({
                        message: replaceTranslationNames(
                            translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED,
                            {
                                '0': translations.STATUS.toLowerCase()
                            }
                        ),
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    }

    useDeepEffect(() => fetchEffectData(fetchStatusOptions), []);

    return (
        <div className={'settings-container data-grid'}>
            <DataGridWrapper
                columns={getDataGridColumns(
                    translations,
                    openEmployeeStatusDialog,
                    fetchStatusOptions
                )}
                deleteHandler={deleteEmployeeStatus}
                filterComponent={getFilterComponent(
                    translations,
                    openEmployeeStatusDialog,
                    fetchStatusOptions
                )}
                getRowId={(row) => row.employeeStatusOptionId}
                initialState={{
                    sorting: {
                        sortModel: [{
                            field: 'name',
                            sort: 'asc'
                        }]
                    }
                }}
                loading={loading}
                rows={statusOptions}
                searchFields={['name', 'color']}
                searchable={true}
                showDeleteColumn={true}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

EmployeeStatusesSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    openConfirmation: PropTypes.func,
    openEmployeeStatusDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openEmployeeStatusDialog(props) {
            dispatch(openDialog(dialogTypes.EMPLOYEE_STATUS_DIALOG, props));
        }
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(EmployeeStatusesSettings);
